import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import MultiSelectDropdown from "../formComponents/MultiSelectDropdown";
import { leadApi, leadSetupApi } from "../../pages/services/leadService";

// ─── Statuses and other fixed lists come from the API (GET /leads/meta) ───────
let metaCache = null;
export function useLeadMeta() {
  const [meta, setMeta] = useState(metaCache);
  useEffect(() => {
    if (metaCache) return;
    leadApi.meta().then((m) => { metaCache = m; setMeta(m); }).catch(() => {});
  }, []);
  return meta;
}

// ─── Dates, always shown in IST ───────────────────────────────────────────────
const IST = { timeZone: "Asia/Kolkata" };
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("en-IN", { ...IST, day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { ...IST, day: "numeric", month: "short", year: "numeric" }) : "—");

// "in 3 h", "2 d overdue"
export function fmtDue(d) {
  if (!d) return "";
  const mins = Math.round((new Date(d) - Date.now()) / 60000);
  const abs = Math.abs(mins);
  const span = abs < 60 ? `${abs} min` : abs < 48 * 60 ? `${Math.round(abs / 60)} h` : `${Math.round(abs / 1440)} d`;
  return mins >= 0 ? `in ${span}` : `${span} overdue`;
}
export const isOverdue = (d) => d && new Date(d) < new Date();

// <input type="datetime-local"> works in the browser's local time.
export const toLocalInput = (d) => {
  if (!d) return "";
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
};
export const fromLocalInput = (s) => (s ? new Date(s).toISOString() : null);
export const inHours = (h) => toLocalInput(new Date(Date.now() + h * 3600000));

// ─── Status pill ──────────────────────────────────────────────────────────────
export const StatusPill = ({ status, label, group }) => (
  <span className={`ld-pill ld-pill-${group || "none"}`} data-status={status}>{label || status}</span>
);

// ─── Campaign filter (multi-select; empty = all) ──────────────────────────────
export function useCampaignOptions() {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    leadSetupApi.campaigns()
      .then((list) => setOptions(list.map((c) => ({ value: c._id, label: `${c.name}${c.isActive ? "" : " (inactive)"}` }))))
      .catch(() => setOptions([]));
  }, []);
  return options;
}

export const CampaignFilter = ({ value, onChange, options }) => (
  <div className="ld-filter">
    <span className="ld-filter-label">Campaign</span>
    <MultiSelectDropdown
      options={options}
      value={value}
      onChange={onChange}
      placeholder="All campaigns"
      showCheckboxes
    />
  </div>
);

// How a lead message is described everywhere (lead page, list accordion).
export const MESSAGE_CHANNEL = { whatsapp: "WhatsApp", email: "Email" };
// A message's channel at a glance: the brand-coloured logo, with its name under it.
const CHANNEL_ICON = { whatsapp: FaWhatsapp, email: MdEmail };
export const ChannelBadge = ({ channel }) => {
  const Icon = CHANNEL_ICON[channel];
  return (
    <span className={`ld-ch-badge is-${channel}`} title={MESSAGE_CHANNEL[channel] || channel}>
      <span className="ld-ch-logo" aria-hidden="true">{Icon ? <Icon /> : "✉"}</span>
      <span className="ld-ch-name">{MESSAGE_CHANNEL[channel] || channel}</span>
    </span>
  );
};
export const MESSAGE_KIND = { dayWise: "Day-wise", demo: "Demo reminder", status: "Status", custom: "Custom", payment: "Payment" };

export const errorText = (e) => (e && e.message) || "Something went wrong";

// Where a lead came from, for people: "meta_instant_form" → "Meta Lead Ads".
const SOURCE_LABELS = { website: "Website", meta_instant_form: "Meta Lead Ads", manual: "Added by hand" };
export const sourceLabel = (s) => SOURCE_LABELS[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");

// Follow-up shortcuts: in 2 hours, tomorrow 10 am, in 2 days 10 am.
export const tomorrowAt = (days, h) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(h, 0, 0, 0); return toLocalInput(d); };
export const FollowUpInput = ({ value, onChange, required }) => (
  <>
    <input type="datetime-local" className="ld-input" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    <div className="ld-badges">
      <button type="button" className="ld-btn ld-btn-sm" onClick={() => onChange(inHours(2))}>+2 hours</button>
      <button type="button" className="ld-btn ld-btn-sm" onClick={() => onChange(tomorrowAt(1, 10))}>Tomorrow 10 AM</button>
      <button type="button" className="ld-btn ld-btn-sm" onClick={() => onChange(tomorrowAt(2, 10))}>In 2 days</button>
      <button type="button" className="ld-btn ld-btn-sm" onClick={() => onChange(tomorrowAt(7, 10))}>Next week</button>
    </div>
  </>
);


// "Refresh status": ask UraanWapp for every sent message's delivery, a batch
// per call, until none is left. call(since) → { checked, changed, remaining,
// rateLimited }; `since` (when the refresh began) keeps a call from asking
// again about a message already checked. onProgress({ checked, changed, remaining }).
const pause = (ms) => new Promise((ok) => setTimeout(ok, ms));
export async function refreshUntilDone(call, onProgress) {
  const since = new Date().toISOString();
  const total = { checked: 0, changed: 0, remaining: 0 };
  for (let round = 0; round < 100; round++) {
    const r = await call(since);
    total.checked += r.checked || 0;
    total.changed += r.changed || 0;
    total.remaining = r.remaining || 0;
    onProgress && onProgress({ ...total });
    if (!total.remaining) break;
    if (r.rateLimited) await pause(5000);            // UraanWapp asked to slow down
    else if (!r.checked) break;                      // no progress: leave the rest to the scheduler
  }
  return total;
}
